import type { GroupId } from 'types/group'
import type { UserSettings } from 'types/user'

import type { GROUP_TYPE } from '@disco/constants/GroupTypes'
import { CUSTOM_CHANNEL } from '@disco/constants/GroupTypes'
import type { SORT_DIRECTION } from '@disco/constants/SortTypes'
import {
  PLAYLIST_DATE_SORT,
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
  TITLE_SORT,
  TRACK_DATE_SORT,
} from '@disco/constants/SortTypes'

/**
 * Gets sort settings for a pane with fallback defaults.
 *
 * Retrieves user-specific sort settings for artists, playlists, and tracks
 * within a group context. Falls back to sensible defaults when user settings
 * are not available. Handles different behavior for custom channels vs regular groups.
 *
 * @param group - The group context containing groupType and groupId
 * @param settings - User settings object containing sort preferences
 * @returns Object containing sort parameters and directions for all content types
 *
 * @example
 * ```typescript
 * const group = { groupType: 'FOLDER', groupId: 'group123' }
 * const settings = {
 *   group_sort_settings: {
 *     'group123': {
 *       track_sort_param: 'title',
 *       track_sort_direction: 'asc'
 *     }
 *   }
 * }
 *
 * const sortSettings = getPaneSortSettingsWithDefaults(group, settings)
 * // {
 * //   artistSortParam: 'name',
 * //   artistSortDirection: 'asc',
 * //   playlistSortParam: 'date_created',
 * //   playlistSortDirection: 'desc',
 * //   trackSortParam: 'title',
 * //   trackSortDirection: 'asc'
 * // }
 * ```
 */
export function getPaneSortSettingsWithDefaults(
  group: {
    groupType?: GROUP_TYPE
    groupId?: GroupId
  },
  settings?: UserSettings | null,
): {
  artistSortDirection: SORT_DIRECTION
  artistSortParam: 'name'
  playlistSortDirection: SORT_DIRECTION
  playlistSortParam: string
  trackSortDirection: SORT_DIRECTION
  trackSortParam: string
} {
  const { groupType, groupId } = group
  const currentSettings = groupId
    ? settings?.group_sort_settings?.[groupId] || {}
    : {}

  return {
    artistSortParam: TITLE_SORT,
    artistSortDirection: SORT_DIRECTION_ASC,
    playlistSortParam:
      groupType !== CUSTOM_CHANNEL
        ? currentSettings.group_playlist_sort_param || PLAYLIST_DATE_SORT
        : currentSettings.channel_playlist_sort_param || TITLE_SORT,
    playlistSortDirection:
      groupType !== CUSTOM_CHANNEL
        ? currentSettings.group_playlist_sort_direction || SORT_DIRECTION_DESC
        : currentSettings.channel_playlist_sort_direction || SORT_DIRECTION_ASC,
    trackSortParam: currentSettings.track_sort_param || TRACK_DATE_SORT,
    trackSortDirection:
      currentSettings.track_sort_direction || SORT_DIRECTION_DESC,
  }
}

/**
 * Sorts items alphabetically by their name property.
 *
 * Performs case-insensitive alphabetical sorting using locale-aware comparison.
 * Designed for objects that have a 'name' property.
 *
 * @param a - First item to compare
 * @param b - Second item to compare
 * @returns Negative number if a < b, positive if a > b, 0 if equal
 *
 * @example
 * ```typescript
 * const items = [
 *   { name: 'zebra' },
 *   { name: 'Apple' },
 *   { name: 'banana' }
 * ]
 *
 * items.sort(sortAlphabetically)
 * // [
 * //   { name: 'Apple' },
 * //   { name: 'banana' },
 * //   { name: 'zebra' }
 * // ]
 * ```
 */
export const sortAlphabetically = (a: any, b: any): number =>
  a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
